import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import gsap from 'gsap';

class Map3D {
    constructor(containerId) {
        console.log("Initializing Map3D with container:", containerId);
        this.container = document.getElementById(containerId);
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 1000);
        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this.controls = null;
        this.people = [];
        this.karaokeBars = [];
        this.saunas = [];
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        this.hoveredPerson = null;
        this.aurora = null;
        this.birds = [];
        this.boats = [];
        this.landmarks = [];
        this.clouds = [];

        this.init();
    }

    init() {
        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.container.appendChild(this.renderer.domElement);

        this.camera.position.set(0, 10, 15);
        this.scene.background = new THREE.Color(0xf0f7f4);

        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.maxPolarAngle = Math.PI / 2.1; // Prevent going below map
        this.controls.minDistance = 5;
        this.controls.maxDistance = 30;

        // Lighting
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        this.scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(5, 10, 5);
        this.scene.add(directionalLight);

        this.createMap();
        this.createPeople();
        this.createKaraokeBars();
        this.createSaunas();
        this.createAurora();
        this.createWildlife();
        this.createLandmarks();
        this.createClouds();
        this.addEventListeners();
        this.animate();
    }

    createMap() {
        const shape = new THREE.Shape();
        
        // Simplified Finland coordinates (scaled to fit)
        const coords = [
            [45, 20], [55, 5], [65, 15], [75, 10], [85, 25], [80, 50], [95, 60], 
            [85, 85], [70, 90], [65, 115], [75, 130], [65, 150], [60, 175], 
            [45, 190], [35, 170], [25, 160], [20, 135], [30, 110], [25, 80], 
            [35, 60], [40, 35]
        ];

        shape.moveTo(coords[0][0] - 50, -(coords[0][1] - 100));
        coords.slice(1).forEach(p => shape.lineTo(p[0] - 50, -(p[1] - 100)));
        shape.closePath();

        const extrudeSettings = {
            steps: 2,
            depth: 2,
            bevelEnabled: true,
            bevelThickness: 0.5,
            bevelSize: 0.5,
            bevelOffset: 0,
            bevelSegments: 5
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        
        // Premium Material: Glass-like glowing green
        const material = new THREE.MeshPhysicalMaterial({
            color: 0x2d5a27,
            transmission: 0.5,
            thickness: 2,
            roughness: 0.2,
            metalness: 0.1,
            ior: 1.5,
            clearcoat: 1,
            emissive: 0x2d5a27,
            emissiveIntensity: 0.2
        });

        const mapMesh = new THREE.Mesh(geometry, material);
        mapMesh.scale.set(0.1, 0.1, 0.1);
        mapMesh.rotation.x = -Math.PI / 2;
        this.scene.add(mapMesh);

        // Add glow effect or subtle particles
        const edges = new THREE.EdgesGeometry(geometry);
        const lineMat = new THREE.LineBasicMaterial({ color: 0xa8d5ba, transparent: true, opacity: 0.5 });
        const line = new THREE.LineSegments(edges, lineMat);
        line.scale.set(0.1, 0.1, 0.1);
        line.rotation.x = -Math.PI / 2;
        this.scene.add(line);

        // Grid helper for depth
        const gridHelper = new THREE.GridHelper(30, 30, 0x2d5a27, 0x2d5a27);
        gridHelper.position.y = -1;
        gridHelper.material.opacity = 0.1;
        gridHelper.material.transparent = true;
        this.scene.add(gridHelper);
    }

    createPeople() {
        const peopleData = [
            { name: "Pekka", x: -3, z: 2, status: "Enjoying a morning coffee in Helsinki.", happiness: 8.5, emoji: "🧔", image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80" },
            { name: "Liisa", x: 0, z: -1, status: "Watching the lakes in Lakeland.", happiness: 9.5, emoji: "👩", image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80" },
            { name: "Matti", x: -2, z: -5, status: "Hiking in Lapland, breathing fresh air.", happiness: 9.0, emoji: "👨", image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80" },
            { name: "Aino", x: 4, z: 4, status: "Dancing to Eurovision tracks!", happiness: 10.0, emoji: "👱‍♀️", image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80" },
            { name: "Sami", x: -5, z: -2, status: "Planning the next sauna session.", happiness: 8.8, emoji: "🧖‍♂️", image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80" }
        ];

        peopleData.forEach(data => {
            const human = this.createLowPolyHuman();
            human.position.set(data.x, 0, data.z);
            human.userData = data;
            
            this.scene.add(human);
            this.people.push(human);
        });
    }

    createLowPolyHuman() {
        const group = new THREE.Group();
        const material = new THREE.MeshStandardMaterial({ color: 0x2d5a27 });

        // Torso
        const torso = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.4, 0.2), material);
        torso.position.y = 0.5;
        group.add(torso);

        // Head
        const head = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.2, 0.2), material);
        head.position.y = 0.8;
        group.add(head);

        // Legs
        const legGeo = new THREE.BoxGeometry(0.1, 0.3, 0.1);
        const leftLeg = new THREE.Mesh(legGeo, material);
        leftLeg.position.set(-0.07, 0.15, 0);
        group.add(leftLeg);

        const rightLeg = new THREE.Mesh(legGeo, material);
        rightLeg.position.set(0.07, 0.15, 0);
        group.add(rightLeg);

        group.leftLeg = leftLeg;
        group.rightLeg = rightLeg;

        return group;
    }

    createKaraokeBars() {
        const barData = [
            { name: "Karaoke Master", x: -2, z: 3, status: "A local favorite for night owls.", happiness: 9.8, type: "karaoke", emoji: "🎤" },
            { name: "Neon Notes", x: 1, z: 2, status: "Glow-in-the-dark singing experience.", happiness: 9.2, type: "karaoke", emoji: "🎶" },
            { name: "The High C", x: -4, z: 0, status: "Where every Finn becomes a star.", happiness: 9.5, type: "karaoke", emoji: "🎤" },
            { name: "Melody Mökki", x: 2, z: -2, status: "Cozy cabin vibes with a microphone.", happiness: 9.0, type: "karaoke", emoji: "🎙️" },
            { name: "Midnight Mic", x: 0, z: 5, status: "Open until the sun never sets.", happiness: 9.7, type: "karaoke", emoji: "🎤" },
            { name: "Sauna Sing", x: -6, z: -1, status: "Sing your heart out after a steam.", happiness: 10.0, type: "karaoke", emoji: "🛁" },
            { name: "Aurora Anthems", x: 3, z: -6, status: "Singing under the Northern Lights.", happiness: 9.4, type: "karaoke", emoji: "✨" },
            { name: "Baltic Beats", x: -1, z: -3, status: "Sea views and classic hits.", happiness: 8.9, type: "karaoke", emoji: "🎵" },
            { name: "Lapland Lyrics", x: 1, z: -8, status: "Reindeer-friendly karaoke spot.", happiness: 9.1, type: "karaoke", emoji: "🦌" },
            { name: "Helsinki Harmonies", x: -3, z: 1, status: "The heart of Finnish karaoke culture.", happiness: 9.9, type: "karaoke", emoji: "🎤" }
        ];

        barData.forEach(data => {
            const group = new THREE.Group();
            
            const baseGeo = new THREE.BoxGeometry(0.3, 0.3, 0.3);
            const baseMat = new THREE.MeshStandardMaterial({ color: 0xff00ff, emissive: 0xff00ff, emissiveIntensity: 0.5 });
            const base = new THREE.Mesh(baseGeo, baseMat);
            base.position.y = 0.15;
            group.add(base);

            const lightGeo = new THREE.SphereGeometry(0.1, 8, 8);
            const lightMat = new THREE.MeshStandardMaterial({ color: 0xffff00, emissive: 0xffff00 });
            const light = new THREE.Mesh(lightGeo, lightMat);
            light.position.y = 0.4;
            group.add(light);

            group.position.set(data.x, 0, data.z);
            group.userData = data;
            
            this.scene.add(group);
            this.karaokeBars.push(group);
        });
    }

    createSaunas() {
        const saunaData = [
            { name: "Löyly Helsinki", x: -4, z: 6, status: "A modern architectural masterpiece by the sea.", happiness: 9.9, type: "sauna", emoji: "🧖‍♂️" },
            { name: "Rajaportin Sauna", x: -1, z: 2, status: "The oldest public sauna in Finland.", happiness: 9.6, type: "sauna", emoji: "🪵" },
            { name: "Sauna Hermanni", x: -3, z: 2, status: "Vintage 50s vibes and community spirit.", happiness: 9.3, type: "sauna", emoji: "🧖‍♀️" },
            { name: "Kulttuurisauna", x: -2, z: 4, status: "Minimalist and ecological urban sauna.", happiness: 9.5, type: "sauna", emoji: "🌿" },
            { name: "Sompasauna", x: -1, z: 5, status: "A self-service, free, community-built sauna.", happiness: 10.0, type: "sauna", emoji: "🔥" }
        ];

        saunaData.forEach(data => {
            const group = new THREE.Group();
            
            const baseGeo = new THREE.BoxGeometry(0.4, 0.4, 0.4);
            const baseMat = new THREE.MeshStandardMaterial({ color: 0x8b4513 }); // Wood brown
            const base = new THREE.Mesh(baseGeo, baseMat);
            base.position.y = 0.2;
            group.add(base);

            // Steam effect (subtle transparent cone)
            const steamGeo = new THREE.ConeGeometry(0.2, 0.6, 8);
            const steamMat = new THREE.MeshStandardMaterial({ color: 0xffffff, transparent: true, opacity: 0.3 });
            const steam = new THREE.Mesh(steamGeo, steamMat);
            steam.position.y = 0.6;
            group.add(steam);

            group.position.set(data.x, 0, data.z);
            group.userData = data;
            
            this.scene.add(group);
            this.saunas.push(group);
        });
    }

    createAurora() {
        const vertexShader = `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `;

        const fragmentShader = `
            uniform float time;
            varying vec2 vUv;
            
            void main() {
                float aurora = sin(vUv.x * 5.0 + time * 0.5) * 0.5 + 0.5;
                aurora *= sin(vUv.y * 2.0 - time * 0.2) * 0.5 + 0.5;
                
                vec3 color1 = vec3(0.0, 1.0, 0.5); // Green
                vec3 color2 = vec3(0.5, 0.0, 1.0); // Purple
                vec3 finalColor = mix(color1, color2, sin(time * 0.1) * 0.5 + 0.5);
                
                gl_FragColor = vec4(finalColor, aurora * 0.4);
            }
        `;

        const geometry = new THREE.CylinderGeometry(15, 15, 8, 32, 1, true);
        this.auroraMaterial = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                time: { value: 0 }
            },
            transparent: true,
            side: THREE.DoubleSide,
            depthWrite: false
        });

        this.aurora = new THREE.Mesh(geometry, this.auroraMaterial);
        this.aurora.position.y = 5;
        this.aurora.visible = false; // Only show in Eurovision theme
        this.scene.add(this.aurora);
    }

    createWildlife() {
        // Create simple birds
        for (let i = 0; i < 5; i++) {
            const bird = new THREE.Group();
            const wingGeo = new THREE.BoxGeometry(0.2, 0.02, 0.1);
            const wingMat = new THREE.MeshStandardMaterial({ color: 0x333333 });
            
            const leftWing = new THREE.Mesh(wingGeo, wingMat);
            leftWing.position.x = -0.1;
            bird.add(leftWing);
            
            const rightWing = new THREE.Mesh(wingGeo, wingMat);
            rightWing.position.x = 0.1;
            bird.add(rightWing);
            
            bird.position.set(Math.random() * 10 - 5, 3 + Math.random() * 2, Math.random() * 10 - 5);
            bird.userData = { speed: 0.02 + Math.random() * 0.02, leftWing, rightWing };
            this.scene.add(bird);
            this.birds.push(bird);
        }

        // Create simple boats
        for (let i = 0; i < 3; i++) {
            const boat = new THREE.Mesh(
                new THREE.BoxGeometry(0.5, 0.2, 0.3),
                new THREE.MeshStandardMaterial({ color: 0xffffff })
            );
            boat.position.set(Math.random() * 10 - 5, -0.9, Math.random() * 10 - 5);
            boat.userData = { angle: Math.random() * Math.PI * 2 };
            this.scene.add(boat);
            this.boats.push(boat);
        }
    }

    updateTheme(theme) {
        const isEurovision = theme === 'eurovision';
        this.scene.background = new THREE.Color(isEurovision ? 0x0d0221 : 0xf0f7f4);
        if (this.aurora) this.aurora.visible = isEurovision;
        
        // Update grid and lines colors
        this.scene.traverse(child => {
            if (child instanceof THREE.GridHelper) {
                child.material.color.setHex(isEurovision ? 0x00ffff : 0x2d5a27);
            }
            if (child instanceof THREE.LineSegments) {
                child.material.color.setHex(isEurovision ? 0x00ffff : 0xa8d5ba);
            }
        });
    }

    createLandmarks() {
        const landmarkData = [
            { name: "Helsinki Cathedral", x: -3.5, z: 1.5, type: "landmark", color: 0xffffff, desc: "The iconic landmark of the capital." },
            { name: "Moomin House", x: -2.5, z: -2.5, type: "landmark", color: 0x4a90e2, desc: "A world of magic in Naantali." },
            { name: "Santa's Workshop", x: 1, z: -8, type: "landmark", color: 0xe74c3c, desc: "Where the magic of Christmas lives year-round." }
        ];

        landmarkData.forEach(data => {
            const group = new THREE.Group();
            
            if (data.name === "Helsinki Cathedral") {
                const base = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.4, 0.6), new THREE.MeshStandardMaterial({ color: 0xffffff }));
                base.position.y = 0.2;
                group.add(base);
                const dome = new THREE.Mesh(new THREE.SphereGeometry(0.2, 16, 16), new THREE.MeshStandardMaterial({ color: 0x006400 }));
                dome.position.y = 0.5;
                group.add(dome);
            } else if (data.name === "Moomin House") {
                const body = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.6, 16), new THREE.MeshStandardMaterial({ color: 0x4a90e2 }));
                body.position.y = 0.3;
                group.add(body);
                const roof = new THREE.Mesh(new THREE.ConeGeometry(0.25, 0.3, 16), new THREE.MeshStandardMaterial({ color: 0xe74c3c }));
                roof.position.y = 0.75;
                group.add(roof);
            } else {
                const cabin = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.4, 0.5), new THREE.MeshStandardMaterial({ color: 0xe74c3c }));
                cabin.position.y = 0.2;
                group.add(cabin);
                const snow = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.05, 0.55), new THREE.MeshStandardMaterial({ color: 0xffffff }));
                snow.position.y = 0.42;
                group.add(snow);
            }

            group.position.set(data.x, 0, data.z);
            group.userData = { ...data, status: data.desc, image: "https://images.unsplash.com/photo-1516738901171-8eb4fc13bd20?w=400&q=80", happiness: 10 };
            this.scene.add(group);
            this.landmarks.push(group);
        });
    }

    createClouds() {
        const cloudMat = new THREE.MeshStandardMaterial({ color: 0xffffff, transparent: true, opacity: 0.8 });
        for (let i = 0; i < 10; i++) {
            const cloud = new THREE.Group();
            const count = 3 + Math.floor(Math.random() * 3);
            for (let j = 0; j < count; j++) {
                const part = new THREE.Mesh(new THREE.SphereGeometry(0.3 + Math.random() * 0.2, 8, 8), cloudMat);
                part.position.set(j * 0.3, Math.random() * 0.2, Math.random() * 0.2);
                cloud.add(part);
            }
            cloud.position.set(Math.random() * 20 - 10, 5 + Math.random() * 2, Math.random() * 20 - 10);
            cloud.userData = { speed: 0.005 + Math.random() * 0.01 };
            this.scene.add(cloud);
            this.clouds.push(cloud);
        }
    }

    addEventListeners() {
        window.addEventListener('resize', () => {
            this.camera.aspect = window.innerWidth / window.innerHeight;
            this.camera.updateProjectionMatrix();
            this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
        });

        this.container.addEventListener('mousemove', (e) => {
            const rect = this.container.getBoundingClientRect();
            this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        });

        this.container.addEventListener('click', () => {
            if (this.hoveredPerson) {
                this.showPersonDetail(this.hoveredPerson.userData);
            } else if (this.hoveredBar) {
                this.showPersonDetail(this.hoveredBar.userData);
            } else if (this.hoveredSauna) {
                this.showPersonDetail(this.hoveredSauna.userData);
            } else if (this.hoveredLandmark) {
                this.showPersonDetail(this.hoveredLandmark.userData);
            }
        });

        // Category Filtering
        const categoryBtns = document.querySelectorAll('.category-btn');
        categoryBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                categoryBtns.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                this.filterMarkers(btn.getAttribute('data-category'));
            });
        });
    }

    filterMarkers(category) {
        const show = (obj, visible) => {
            gsap.to(obj.scale, {
                x: visible ? 1 : 0,
                y: visible ? 1 : 0,
                z: visible ? 1 : 0,
                duration: 0.3,
                onComplete: () => {
                    obj.visible = visible;
                }
            });
            if (visible) obj.visible = true;
        };

        this.people.forEach(p => show(p, category === 'all' || category === 'people'));
        this.karaokeBars.forEach(b => show(b, category === 'all' || category === 'karaoke'));
        this.saunas.forEach(s => show(s, category === 'all' || category === 'sauna'));
        this.landmarks.forEach(l => show(l, category === 'all'));
    }

    showPersonDetail(data) {
        const detail = document.getElementById('person-detail');
        document.getElementById('personName').textContent = data.name;
        document.getElementById('personStatus').textContent = data.status;
        document.getElementById('happinessGrade').textContent = data.happiness;
        
        const segment = document.getElementById('donutSegment');
        const percentage = data.happiness * 10;
        segment.style.strokeDasharray = `${percentage}, 100`;
        
        document.getElementById('personPhoto').src = data.image;
        
        detail.style.display = 'flex';
        gsap.fromTo(detail, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.5 });
    }

    animate() {
        requestAnimationFrame(this.animate.bind(this));
        this.controls.update();

        // Raycasting
        this.raycaster.setFromCamera(this.mouse, this.camera);
        const allInteractions = [...this.people, ...this.karaokeBars, ...this.saunas, ...this.landmarks];
        const intersects = this.raycaster.intersectObjects(allInteractions, true);

        if (intersects.length > 0) {
            let obj = intersects[0].object;
            while(obj.parent && !allInteractions.includes(obj)) {
                obj = obj.parent;
            }
            
            if (this.karaokeBars.includes(obj)) {
                this.hoveredBar = obj;
                this.hoveredPerson = null;
                this.hoveredSauna = null;
                this.hoveredLandmark = null;
                gsap.to(obj.scale, { x: 1.5, y: 1.5, z: 1.5, duration: 0.2 });
            } else if (this.saunas.includes(obj)) {
                this.hoveredSauna = obj;
                this.hoveredPerson = null;
                this.hoveredBar = null;
                this.hoveredLandmark = null;
                gsap.to(obj.scale, { x: 1.5, y: 1.5, z: 1.5, duration: 0.2 });
            } else if (this.people.includes(obj)) {
                this.hoveredPerson = obj;
                this.hoveredBar = null;
                this.hoveredSauna = null;
                this.hoveredLandmark = null;
                gsap.to(obj.scale, { x: 1.5, y: 1.5, z: 1.5, duration: 0.2 });
            } else if (this.landmarks.includes(obj)) {
                this.hoveredLandmark = obj;
                this.hoveredPerson = null;
                this.hoveredBar = null;
                this.hoveredSauna = null;
                gsap.to(obj.scale, { x: 1.5, y: 1.5, z: 1.5, duration: 0.2 });
            }
        } else {
            if (this.hoveredPerson) gsap.to(this.hoveredPerson.scale, { x: 1, y: 1, z: 1, duration: 0.2 });
            if (this.hoveredBar) gsap.to(this.hoveredBar.scale, { x: 1, y: 1, z: 1, duration: 0.2 });
            if (this.hoveredSauna) gsap.to(this.hoveredSauna.scale, { x: 1, y: 1, z: 1, duration: 0.2 });
            if (this.hoveredLandmark) gsap.to(this.hoveredLandmark.scale, { x: 1, y: 1, z: 1, duration: 0.2 });
            this.hoveredPerson = null;
            this.hoveredBar = null;
            this.hoveredSauna = null;
            this.hoveredLandmark = null;
        }

        // Subtle movement for people
        this.people.forEach(person => {
            person.position.x += Math.sin(Date.now() * 0.001 + person.userData.x) * 0.002;
            person.position.z += Math.cos(Date.now() * 0.001 + person.userData.z) * 0.002;

            // Animate walking legs
            const walkCycle = Math.sin(Date.now() * 0.01);
            person.leftLeg.rotation.x = walkCycle * 0.5;
            person.rightLeg.rotation.x = -walkCycle * 0.5;

            // Rotate towards movement direction (simplified)
            person.rotation.y = Math.atan2(Math.sin(Date.now() * 0.001 + person.userData.x), Math.cos(Date.now() * 0.001 + person.userData.z));
        });

        // Glow effect for bars
        this.karaokeBars.forEach(bar => {
            bar.children[1].material.emissiveIntensity = 0.5 + Math.sin(Date.now() * 0.005) * 0.5;
        });

        // Aurora animation
        if (this.aurora && this.aurora.visible) {
            this.auroraMaterial.uniforms.time.value += 0.01;
            this.aurora.rotation.y += 0.001;
        }

        // Wildlife animation
        this.birds.forEach(bird => {
            bird.position.x += bird.userData.speed;
            if (bird.position.x > 15) bird.position.x = -15;
            
            const wingFlap = Math.sin(Date.now() * 0.01) * 0.5;
            bird.userData.leftWing.rotation.z = wingFlap;
            bird.userData.rightWing.rotation.z = -wingFlap;
        });

        this.boats.forEach(boat => {
            boat.userData.angle += 0.005;
            boat.position.x += Math.sin(boat.userData.angle) * 0.005;
            boat.position.z += Math.cos(boat.userData.angle) * 0.005;
            boat.rotation.y = boat.userData.angle;
        });

        this.clouds.forEach(cloud => {
            cloud.position.x += cloud.userData.speed;
            if (cloud.position.x > 15) cloud.position.x = -15;
        });

        this.renderer.render(this.scene, this.camera);
    }
}

export default Map3D;
